// 予算のCRUD・自動生成（CONTRACTS.md §3）

import { db } from './db';
import type { Budget } from './types';
import { listCategories } from './categories';

function budgetId(categoryId: string, yearMonth: string): string {
  return `${categoryId}:${yearMonth}`;
}

/**
 * 'YYYY-MM' 形式の月文字列から前月の文字列を求める。
 * lib/date.ts（FOUNDATION担当）への依存を避けるため、data層内で完結させる。
 */
function getPreviousYearMonthLocal(yearMonth: string): string {
  const [yearStr, monthStr] = yearMonth.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
}

/**
 * アプリ起動時に一度呼ぶ。当月分の予算レコードが無いカテゴリについて、
 * 前月レコードがあれば金額を引き継いで作成、無ければ amount: 0 で作成する。
 */
export async function ensureCurrentMonthBudgets(yearMonth: string): Promise<void> {
  const categories = await listCategories();
  const prevYearMonth = getPreviousYearMonthLocal(yearMonth);

  await db.transaction('rw', db.budgets, async () => {
    for (const category of categories) {
      const id = budgetId(category.id, yearMonth);
      const existing = await db.budgets.get(id);
      if (existing) continue;

      const prevBudget = await db.budgets.get(budgetId(category.id, prevYearMonth));
      const amount = prevBudget ? prevBudget.amount : 0;

      const budget: Budget = {
        id,
        categoryId: category.id,
        yearMonth,
        amount,
      };
      await db.budgets.put(budget);
    }
  });
}

export async function getBudget(
  categoryId: string,
  yearMonth: string
): Promise<Budget | undefined> {
  return db.budgets.get(budgetId(categoryId, yearMonth));
}

export async function setBudgetAmount(
  categoryId: string,
  yearMonth: string,
  amount: number
): Promise<void> {
  const budget: Budget = {
    id: budgetId(categoryId, yearMonth),
    categoryId,
    yearMonth,
    amount,
  };
  await db.budgets.put(budget);
}

export async function listBudgetsByMonth(yearMonth: string): Promise<Budget[]> {
  return db.budgets.where('yearMonth').equals(yearMonth).toArray();
}
